//core
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Box, FormHelperText, Typography } from "@mui/material";
import { Scrollbars } from "react-custom-scrollbars";
import Button from "../../../../../UI/Buttons/Button";

//styles
import { useStyles } from "./styles";

//hooks
import { useWebhooksReport } from "../../../../../../store/admin/reports/webhooks_report/useWebhooksReport";

//selectors
import {
  selectBulkResendPostbackData,
  selectBulkResendPostbackIsFetching,
} from "../../../../../../store/admin/reports/webhooks_report/selectors";

//utils
import { getDataIds } from "../../../utils";

//components
import InfoAlert from "../../../../../UI/InfoAlert";

interface IBulkResendPostbackProps {
  webhooks: any[];
  setToggleModal: (state: boolean) => void;
  onSuccess: () => void;
}

const BulkResendPostback: React.FC<IBulkResendPostbackProps> = ({
  webhooks,
  setToggleModal,
  onSuccess,
}): JSX.Element => {
  const { t } = useTranslation();
  const styles = useStyles();

  const { bulkResendPostback, clearBulkResendPostbackState } = useWebhooksReport();

  const bulkResendPostbackData = useSelector(selectBulkResendPostbackData);
  const isFetching = useSelector(selectBulkResendPostbackIsFetching);

  const [items, setItems] = useState<any[]>(webhooks);
  const hasSubmitted = useRef(false);

  useEffect(() => {
    clearBulkResendPostbackState();

    return () => {
      clearBulkResendPostbackState();
    };
  }, []);

  useEffect(() => {
    if (hasSubmitted.current && !!bulkResendPostbackData) {
      onSuccess();
      setToggleModal(false);
    }
  }, [bulkResendPostbackData]);

  const handleRemoveDone = () => {
    setItems((prevState) => prevState.filter((item) => item.status !== 'done'));
  };

  const handleCancel = () => {
    setToggleModal(false);
  };

  const handleResend = () => {
    hasSubmitted.current = true;
    bulkResendPostback({ taskIds: getDataIds(items) });
  };

  const hasDoneItems = items.some((item) => item.status === 'done');

  return (
    <>
      <Typography className={styles.title} variant="h6">
        {t("admin.reports.webhooks_report.resendPostback.title")}
      </Typography>
      <Typography className={styles.description} variant="body2" color="secondary">
        {t("admin.reports.webhooks_report.resendPostback.description")}
      </Typography>

      <InfoAlert
        text={t("admin.reports.webhooks_report.resendPostback.info")}
        actionLabel={`${t("common.buttons.remove")} Done`}
        actionDisabled={!hasDoneItems}
        onAction={handleRemoveDone}
      />

      {items?.length > 0 && (
        <Box className={styles.list}>
          <Scrollbars
            className={styles.customScroll}
            autoHeight
            autoHeightMin={0}
            autoHeightMax={280}
            hideTracksWhenNotNeeded={true}
            renderView={(props) => <div {...props} className="view" />}
            renderTrackHorizontal={(props) => (
              <div {...props} className="track-horizontal" />
            )}
            renderTrackVertical={(props) => (
              <div {...props} className="track-vertical" />
            )}
          >
            {items.map((item) => (
              <Box className={styles.listItem} key={item.id}>
                <Typography className={styles.listItemId} variant="body1">
                  {item.id}
                </Typography>
                <Typography className={styles.listItemStatus} variant="caption">
                  {t(
                    `admin.reports.webhooks_report.webhooks_statuses.${item.status}`,
                  )}
                </Typography>
              </Box>
            ))}
          </Scrollbars>
        </Box>
      )}

      <Box className={styles.actions}>
        <Button
          color="secondary"
          disableElevation
          type="button"
          variant="contained"
          disabled={!items.length || isFetching}
          onClick={handleResend}
        >
          {t("common.buttons.resend")}
        </Button>
        <Button
          color="secondary"
          disableElevation
          type="button"
          variant="outlined"
          onClick={handleCancel}
        >
          {t("common.buttons.cancel")}
        </Button>
      </Box>
      <FormHelperText className={styles.helperText}>
        {t("admin.reports.webhooks_report.resendPostback.helperText")}
      </FormHelperText>
    </>
  );
};

export default BulkResendPostback;
